import React from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import './CustomTable.css';
import { CustomTableType } from './types/CustomTableType';
import { CustomPagination } from '../CustomPagination/CustomPagination';

/**
 * CustomTable Component
 * A highly customizable, fully responsive enterprise table component
 * with built-in pagination, horizontal touch-scrolling, and rich aesthetics.
 */
export function CustomTable<T extends { id: string | number }>({
    columns,
    data,
    isLoading = false,
    isCompact = false,
    emptyMessage = 'No data available',
    emptyIcon,
    onRowClick,
    className = '',
    rowClassName,
    minWidth,
    pagination,
    sortBy,
    sortOrder,
    onSort,
}: CustomTableType<T>) {
    // Dynamic default min-width based on number of columns
    const calculatedMinWidth = minWidth || (columns.length >= 6 ? '780px' : columns.length >= 4 ? '620px' : '100%');

    const handleHeaderClick = (colKey: string, isSortable?: boolean) => {
        if (!isSortable || !onSort) return;
        if (sortBy === colKey) {
            onSort(colKey, sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            onSort(colKey, 'asc');
        }
    };

    return (
        <div className={`custom-table-container ${className}`}>
            <div className="custom-table-wrapper">
                <table
                    className={`custom-table ${isCompact ? 'compact' : ''}`}
                    style={{ minWidth: calculatedMinWidth }}
                >
                    <thead>
                        <tr>
                            {columns.map((col) => {
                                const sortKey = col.sortKey || col.key;
                                const isSortable = col.sortable && !!onSort;
                                const isSorted = isSortable && sortBy === sortKey;

                                return (
                                    <th
                                        key={col.key}
                                        style={{
                                            width: col.width,
                                            minWidth: col.minWidth,
                                            cursor: isSortable ? 'pointer' : 'default',
                                            userSelect: isSortable ? 'none' : 'auto',
                                        }}
                                        className={`${col.align ? `text-${col.align}` : 'text-left'} ${isSortable ? 'custom-table__th--sortable' : ''} ${isSorted ? 'custom-table__th--sorted' : ''}`}
                                        onClick={() => handleHeaderClick(sortKey, isSortable)}
                                        title={isSortable ? `Sort by ${col.header}` : undefined}
                                    >
                                        <div className={`custom-table__th-content ${col.align === 'right' ? 'justify-end' : col.align === 'center' ? 'justify-center' : 'justify-start'}`}>
                                            <span>{col.header}</span>
                                            {isSortable && (
                                                <span className="custom-table__sort-icon">
                                                    {isSorted ? (
                                                        sortOrder === 'asc' ? <ArrowUp size={13} /> : <ArrowDown size={13} />
                                                    ) : (
                                                        <ArrowUpDown size={13} className="custom-table__sort-icon--idle" />
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            Array.from({ length: 5 }).map((_, idx) => (
                                <tr key={`skeleton-${idx}`}>
                                    {columns.map((col) => (
                                        <td
                                            key={`skeleton-td-${col.key}`}
                                            style={{ minWidth: col.minWidth }}
                                        >
                                            <div className="skeleton-row" style={{ height: '20px', borderRadius: '4px' }} />
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : data?.length > 0 ? (
                            data.map((item) => (
                                <tr
                                    key={item.id}
                                    onClick={() => onRowClick?.(item)}
                                    className={`${onRowClick ? 'clickable' : ''} ${rowClassName ? rowClassName(item) : ''}`}
                                >
                                    {columns.map((col) => (
                                        <td
                                            key={`${item.id}-${col.key}`}
                                            style={{ minWidth: col.minWidth }}
                                            className={col.align ? `text-${col.align}` : 'text-left'}
                                        >
                                            {col.render ? col.render(item) : (item as any)[col.key]}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : null}
                    </tbody>
                </table>

                {!isLoading && (!data || data.length === 0) && (
                    <div className="custom-table__empty">
                        {emptyIcon && <div className="custom-table__empty-icon">{emptyIcon}</div>}
                        <h3 className="custom-table__empty-text">{emptyMessage}</h3>
                        <p className="custom-table__empty-subtext">Try adjusting your filters or adding a new record.</p>
                    </div>
                )}
            </div>

            {pagination && (
                <CustomPagination {...pagination} />
            )}
        </div>
    );
}

export default CustomTable;
